import type { CSSProperties, ElementType } from "react";
import React, { useImperativeHandle, useRef, useState } from "react";

import cn from "classnames";

import { TextCoreProvider, useTextCoreStore } from "./store/store";
import { TEXT_CORE_DISPLAY_NAME } from "./utils/core";
import { LIST_VARIANTS_MAP } from "../../../components/Text/Text/constants/constants";
import { useIsomorphicLayoutEffect } from "../../../hooks";
import { Box, boxForwardRef } from "../Box/Box";

import type { TextCoreProps } from "./types";
import type { BoxComponentWithDefault } from "../Box/Box";

import classes from "./TextCore.styl";

export const TEXT_CORE_TEST_ID = "text-core-test-id";

export interface TextCoreCssProperties extends CSSProperties {
    "--count-of-line"?: TextCoreProps["maxLines"];
}

type PolymorphicTextCoreProps = BoxComponentWithDefault<
    "span",
    TextCoreProps,
    "className" | "style"
>;

export const TextCore = boxForwardRef<PolymorphicTextCoreProps>(
    (
        {
            underline,
            strikethrough,
            asList,
            typographyStyle,
            textCase,
            alignmentX,
            lineHeight,
            italic,
            quote,
            children,
            ...props
        },
        ref,
    ) => {
        return (
            // eslint-disable-next-line ok-custom-rules/check-attribute
            <TextCoreProvider
                strikethrough={strikethrough}
                underline={underline || "none"}
                asList={asList}
                typographyStyle={typographyStyle}
                textCase={textCase}
                alignmentX={alignmentX}
                lineHeight={lineHeight}
                italic={italic}
                quote={quote}
            >
                <TextCoreContent {...props} ref={ref}>
                    {children}
                </TextCoreContent>
            </TextCoreProvider>
        );
    },
);

export const TextCoreContent = boxForwardRef<PolymorphicTextCoreProps>(
    (
        {
            as = "span",
            width = "auto",
            maxLines = "none",
            truncateStyle = "ellipsis",
            textContainerClassName,
            textClassName,
            customVarsStyles,
            "data-tsid": tsid,
            children,
            ...props
        },
        ref,
    ) => {
        const textRef = useRef<HTMLSpanElement>(null);
        const [isOverflowing, setIsOverflowing] = useState(false);

        const {
            underline = "none",
            strikethrough,
            asList,
            typographyStyle = "body-l",
            textCase = "asTyped",
            alignmentX = "start",
            lineHeight,
            italic,
            quote,
        } = useTextCoreStore();

        const asValue: ElementType = asList ? LIST_VARIANTS_MAP[asList] : as;

        const textStyle: TextCoreCssProperties = {
            "--count-of-line": maxLines,
            ...(lineHeight && {
                "--line-height": lineHeight,
            }),
        };

        const textContainerStyle = {
            ...textStyle,
            width,
        };

        const isTruncatable = !asList && maxLines && maxLines !== "none";

        const textClasses = cn(
            classes["text-core"],
            typographyStyle !== "custom" && classes[`__${typographyStyle}`],
            classes[`__alignment-x-${alignmentX}`],
            textCase !== "asTyped" && classes[`__case-${textCase}`],
            underline !== "none" && classes[`__underline-${underline}`],
            {
                [classes["__strikethrough"]]: strikethrough,
                [classes["__italic"]]: italic,
                [classes["__cited"]]: quote,
                [classes["__truncate_one-line"]]:
                    isTruncatable &&
                    maxLines === 1 &&
                    truncateStyle === "ellipsis",
                [classes["__truncate_multi-line"]]:
                    isTruncatable && maxLines > 1 && truncateStyle !== "none",
                [classes["__truncate-none"]]:
                    isTruncatable && truncateStyle === "none" && maxLines > 1,
                [classes[`__height-count-of-lines`]]:
                    isTruncatable && maxLines === 1 && truncateStyle === "fade",
                [classes[`__show-right-fade`]]:
                    isTruncatable &&
                    maxLines === 1 &&
                    truncateStyle === "fade" &&
                    isOverflowing,
            },
            textClassName,
            customVarsStyles,
        );

        const textContainerClasses = cn(
            classes["text-core-container"],
            {
                [classes[
                    `__alignment-x-${alignmentX}` as keyof typeof classes
                ]]: alignmentX !== "start",
                [classes[`__truncate_one-line`]]:
                    !asList && maxLines === 1 && truncateStyle !== "none",
            },
            textContainerClassName,
            customVarsStyles,
        );

        useIsomorphicLayoutEffect(() => {
            const text = textRef.current;
            if (!text) return;

            const checkOverflow = () => {
                setIsOverflowing(text.scrollWidth > text.clientWidth);
            };

            checkOverflow();

            const resizeObserver = new ResizeObserver(checkOverflow);
            resizeObserver.observe(text);

            return () => resizeObserver.disconnect();
        }, [truncateStyle]);

        /** Устанавливаем ref, полученному от родителя, значение от textRef.current */
        useImperativeHandle(ref, () => textRef.current);

        return (
            <span
                data-tsid={tsid || TEXT_CORE_TEST_ID}
                data-uikit={TEXT_CORE_DISPLAY_NAME}
                className={textContainerClasses}
                style={textContainerStyle}
                ref={ref}
            >
                <Box
                    as={asValue}
                    style={textStyle}
                    className={textClasses}
                    role={props.role || (quote ? "blockquote" : undefined)}
                    {...props}
                    ref={textRef}
                >
                    {children}
                </Box>
            </span>
        );
    },
);

TextCore.displayName = TEXT_CORE_DISPLAY_NAME;

export default TextCore;
